import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import { Sidebar } from "@/components/Sidebar";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import ProjectDetail from "@/pages/ProjectDetail";
import ProjectWizard from "@/pages/ProjectWizard";
import Presenter from "@/pages/Presenter";
import PresenterEditor from "@/pages/PresenterEditor";
import ProductionMode from "@/pages/ProductionMode";
import OnboardingPage from "@/pages/OnboardingPage";
import TeamSettings from "@/pages/TeamSettings";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Storyboard from "@/pages/Storyboard";
import PublicShare from "@/pages/PublicShare";
import InviteAccept from "@/pages/InviteAccept";
import ResetPassword from "@/pages/ResetPassword";
import { JobsProvider } from "@/contexts/JobsContext";
import { RatesProvider } from "@/contexts/RatesContext";
import { ActivityPanel } from "@/components/ActivityPanel";
import { ChatWidget } from "@/components/ChatWidget";
import { UtilityDock } from "@/components/UtilityDock";
import { DockProvider } from "@/contexts/DockContext";
import { PendingInviteModal } from "@/components/PendingInviteModal";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#060608] flex items-center justify-center">
      <div className="w-48 h-1 bg-[#121216] rounded-full overflow-hidden">
        <div className="h-full w-1/2 bg-[#FF4500] rounded-full animate-loading-bar" />
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (user.onboarding_completed === false && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" />;
  }
  return children;
}

function AppLayout({ children }) {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 md:ml-[68px] pb-20 md:pb-0">
        {children}
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#060608] flex items-center justify-center">
      <div className="w-48 h-1 bg-[#121216] rounded-full overflow-hidden">
        <div className="h-full w-1/2 bg-[#FF4500] rounded-full animate-loading-bar" />
      </div>
    </div>
  );

  return (
    <>
    <Routes>
      {/* Public routes - no sidebar */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <AuthPage />} />
      <Route path="/onboarding" element={user ? <OnboardingPage /> : <Navigate to="/login" />} />
      <Route path="/share/:token" element={<PublicShare />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />

      {/* Authenticated routes - with sidebar */}
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><ErrorBoundary><Dashboard /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><ErrorBoundary><TeamSettings /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/presenter" element={<ProtectedRoute><AppLayout><ErrorBoundary><Presenter /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/presenter/:id" element={<ProtectedRoute><AppLayout><ErrorBoundary><PresenterEditor /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/project/new" element={<ProtectedRoute><AppLayout><ErrorBoundary><ProjectWizard /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/project/:id" element={<ProtectedRoute><AppLayout><ErrorBoundary><ProjectDetail /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/project/:projectId/scenes/:sceneId" element={<ProtectedRoute><AppLayout><ErrorBoundary><ProductionMode /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/characters" element={<Navigate to="/dashboard?tab=characters" replace />} />
      <Route path="/locations" element={<Navigate to="/dashboard?tab=locations" replace />} />
      <Route path="/project/:projectId/production" element={<ProtectedRoute><AppLayout><ErrorBoundary><ProductionMode /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="/project/:id/storyboard" element={<ProtectedRoute><AppLayout><ErrorBoundary><Storyboard /></ErrorBoundary></AppLayout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
      {user && <UtilityDock />}
      {user && <ActivityPanel />}
      {user && <ChatWidget />}
      {user && <PendingInviteModal />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <JobsProvider>
        <RatesProvider>
        <DockProvider>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: { background: '#1A1A20', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }
          }}
        />
        <AppRoutes />
        </DockProvider>
        </RatesProvider>
        </JobsProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
